import React, { useEffect, useRef } from "react";
import {
  trackCalendlyEmbed,
  trackConsultationScheduled,
} from "@/utils/eventTracking";

// Calendly TypeScript declarations
declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement | null;
        prefill?: Record<string, unknown>;
        utm?: Record<string, unknown>;
      }) => void;
    };
  }
}

type CalendlyEmbedProps = {
  title?: string;
  className?: string;
  height?: number | string;
  eventType?: "general" | "wedding" | "corporate" | "private_party";
  location?: string;
  disableViewTracking?: boolean; // Disable view tracking when embed is in a modal
};

const CalendlyEmbed: React.FC<CalendlyEmbedProps> = ({
  title = "Book a consultation — Calendly",
  className,
  height = 780,
  eventType = "general",
  location = "unknown",
  disableViewTracking = false,
}) => {
  const src = (import.meta as any).env?.VITE_CALENDLY_URL;
  const embedRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);

  // Track when Calendly embed comes into view
  useEffect(() => {
    // Skip tracking if disabled (e.g., when embed is in a modal that tracks modal_open instead)
    if (disableViewTracking || !embedRef.current || hasTrackedView.current)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            // Additional checks: make sure element is actually visible (not in a hidden modal)
            const element = entry.target as HTMLElement;

            // Check 1: offsetParent is null for hidden elements
            const hasOffsetParent = element.offsetParent !== null;

            // Check 2: Element must be visible (not display:none, visibility:hidden, or opacity:0)
            const styles = window.getComputedStyle(element);
            const isDisplayed = styles.display !== "none";
            const isVisible = styles.visibility !== "hidden";
            const hasOpacity = parseFloat(styles.opacity) > 0;

            // Check 3: Element must have actual dimensions
            const hasDimensions =
              element.offsetWidth > 0 && element.offsetHeight > 0;

            // Only track if ALL checks pass
            if (
              hasOffsetParent &&
              isDisplayed &&
              isVisible &&
              hasOpacity &&
              hasDimensions
            ) {
              trackCalendlyEmbed("view", eventType, location);
              hasTrackedView.current = true;
            }
          }
        });
      },
      { threshold: 0.3 },
    ); // Track when 30% visible
    observer.observe(embedRef.current);

    return () => {
      if (embedRef.current) {
        observer.unobserve(embedRef.current);
      }
    };
  }, [eventType, location, disableViewTracking]);

  // Listen for Calendly booking completion events
  useEffect(() => {
    // Load Calendly widget.js if not already loaded
    const loadCalendlyWidget = () => {
      if (
        typeof window !== "undefined" &&
        !document.querySelector(
          'script[src*="calendly.com/assets/external/widget"]',
        )
      ) {
        const script = document.createElement("script");
        script.src = "https://assets.calendly.com/assets/external/widget.js";
        script.async = true;
        document.head.appendChild(script);
      }
    };

    loadCalendlyWidget();

    // Listen for Calendly event scheduled
    const handleCalendlyEvent = (e: Event) => {
      const customEvent = e as CustomEvent;

      // Calendly fires 'calendly.event_scheduled' when a consultation is scheduled (lead capture)
      if (customEvent.detail && customEvent.detail.event) {
        const eventData = customEvent.detail.event;

        // Track the consultation scheduled (lead capture) with Calendly data
        trackConsultationScheduled({
          eventUri: eventData.uri,
          inviteeUri: customEvent.detail.invitee?.uri,
          eventTypeName: eventData.name,
        });
      }
    };

    // Listen for the Calendly event
    window.addEventListener(
      "calendly.event_scheduled",
      handleCalendlyEvent as EventListener,
    );

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener(
        "calendly.event_scheduled",
        handleCalendlyEvent as EventListener,
      );
    };
  }, []); // Empty deps - listener stays for component lifetime

  if (!src) {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(
        "VITE_CALENDLY_URL is not set. Calendly embed won't render.",
      );
    }
    return (
      <div className={className} data-oid="_6brh6a">
        <div
          className="w-full rounded-lg border border-border/50 bg-white/80 p-6 text-center text-sm text-muted-foreground"
          data-oid="8jl.tes"
        >
          Calendly is not configured. Please set VITE_CALENDLY_URL in your
          environment.
        </div>
      </div>
    );
  }

  // Initialize Calendly inline widget when component mounts
  useEffect(() => {
    if (!embedRef.current || !src) return;

    // Wait for Calendly widget to be available
    const initCalendly = () => {
      if (typeof window !== "undefined" && window.Calendly) {
        window.Calendly.initInlineWidget({
          url: src,
          parentElement: embedRef.current,
          prefill: {},
          utm: {},
        });
      } else {
        // If Calendly not loaded yet, try again in 100ms
        setTimeout(initCalendly, 100);
      }
    };

    initCalendly();
  }, [src]);

  return (
    <div
      ref={embedRef}
      className={className}
      style={{
        minHeight: typeof height === "number" ? height : 560,
        height: typeof height === "number" ? height : 560,
      }}
      data-oid="jk4w:nz"
    />
  );
};

export default CalendlyEmbed;
